import ArrowDownRight from "@icons/arrow-down-right.svg?react";
import ArrowUpRight from "@icons/arrow-up-right.svg?react";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ScriptableContext,
  Tooltip,
  TooltipModel,
} from "chart.js";
import React from "react";
import { z } from "zod";

import {
  currencyFormatter,
  numberFormatter,
} from "@/common/services/formatter";
import { CurrencyRatesToCurrency1 } from "@/generated/api/prices";

import { positionTooltip } from "./chart.utils";

ChartJS.register(CategoryScale);
ChartJS.register(LineController);
ChartJS.register(LinearScale);
ChartJS.register(PointElement);
ChartJS.register(LineElement);
ChartJS.register(Filler);
ChartJS.register(Tooltip);

type ChartData = {
  datetime: string;
  rate?: number;
};

type ChartProps = {
  tooltip?: (label: string, datasetIndex: number) => string;
  currency: CurrencyRatesToCurrency1;
  data: {
    current: ChartData[];
    next?: ChartData[];
  };
};

const Chart = ({ currency, data, tooltip, ...props }: ChartProps) => {
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const lineRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const existingChart = ChartJS.getChart(chartRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    const chart = new ChartJS(chartRef.current, {
      data: {
        datasets: (Object.keys(data) as (keyof typeof data)[]).map((key) => ({
          backgroundColor: getBackgroundColor,
          borderColor: getBorderColor,
          borderDash: key === "current" ? [] : [8, 2],
          borderWidth: 3,
          data: data[key],
          fill: key === "current",
          name: key,
          parsing: {
            xAxisKey: "datetime",
            yAxisKey: "rate",
          },
          pointRadius: 0,
        })),
      },
      options: {
        animation: false,
        interaction: {
          axis: "x",
          mode: "nearest",
        },
        layout: {
          padding: 0,
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              afterBody: ([item]) => item.label,
              label: ({ raw, datasetIndex }) => {
                const tooltipSchema = z.object({
                  datetime: z.string(),
                  rate: z.number(),
                });
                const safeRaw = tooltipSchema.parse(raw);

                const label = currencyFormatter(currency, {
                  maximumFractionDigits: 2,
                }).format(safeRaw.rate);
                return tooltip ? tooltip(label, datasetIndex) : label;
              },
              title: () => "",
            },
            displayColors: false,
            enabled: false,
            external: (args) =>
              externalTooltipHandler(
                args,
                tooltipRef.current!,
                lineRef.current!,
              ),
            intersect: false,
            position: "nearest",
            usePointStyle: true,
          },
        },
        responsive: true,
        scales: {
          x: {
            border: {
              color: "#caccd1",
            },
            grid: {
              color: "transparent",
            },
            ticks: {
              align: "inner",
              autoSkip: true,
            },
          },
          y: {
            beginAtZero: false,
            border: {
              color: "transparent",
              dash: [2, 2],
            },
            grid: {
              tickColor: (context) =>
                context.index === 0 ? "#caccd1" : "transparent",
              tickLength: 10,
            },
            ticks: {
              callback: (value) => {
                if (value === 0) {
                  return "";
                }
                return numberFormatter({
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).format(
                  typeof value === "number" ? value : parseFloat(value),
                );
              },
            },
          },
        },
      },
      type: "line",
    });

    return () => {
      chart.destroy();
    };
  }, [currency, data, tooltip]);

  return (
    <div className="relative h-[200px] sm:h-[280px]">
      <div ref={lineRef} />
      <canvas className="mr-4 max-h-[280px]" ref={chartRef} {...props} />
      <div
        className="pointer-events-none absolute whitespace-nowrap rounded-lg bg-[#4D5570CC] text-xs text-white"
        data-testid="chart-tooltip"
        ref={tooltipRef}
      />
    </div>
  );
};

const Container = ({ children }: React.PropsWithChildren) => (
  <div className="flex w-full flex-col gap-y-5 self-start">{children}</div>
);

const Price = ({ children, ...rest }: React.PropsWithChildren) => {
  return (
    <h3 className="text-can-midnight-steel self-start font-bold" {...rest}>
      {children}
    </h3>
  );
};

const Title = ({ children }: React.PropsWithChildren) => {
  return (
    <h2 className="text-can-midnight-steel flex min-h-7 items-center gap-x-3 text-xl font-bold">
      {children}
    </h2>
  );
};

const Trend = ({
  currency,
  children,
  className,
}: {
  currency: CurrencyRatesToCurrency1;
  className?: string;
  children: number;
}) => {
  let Icon: React.ComponentType | undefined;
  let color = "text-can-midnight-steel";
  z.number().parse(children);

  if (children > 0) {
    Icon = ArrowUpRight;
    color = "text-can-forest-teal";
  }

  if (children < 0) {
    Icon = ArrowDownRight;
    color = "text-[#E55743]";
  }

  return (
    <b
      className={`${color} flex items-center gap-x-2 font-bold ${
        className ?? ""
      }`}
    >
      {currencyFormatter(currency).format(children)}
      {Icon ? <Icon /> : null}
    </b>
  );
};

const Source = ({ children }: React.PropsWithChildren) => {
  return <small className="basis-full text-xs">{children}</small>;
};

const ChartContainer = ({ children }: React.PropsWithChildren) => {
  return (
    <div className="shadow-can-light-box flex min-h-[236px] w-full flex-col justify-center gap-y-5 self-start rounded-2xl p-4 pr-0">
      {children}
    </div>
  );
};

Chart.Container = Container;
Chart.Price = Price;
Chart.Trend = Trend;
Chart.Source = Source;
Chart.Title = Title;
Chart.ChartContainer = ChartContainer;

const getBackgroundColor = (context: ScriptableContext<"line">) => {
  const { ctx, chartArea } = context.chart;

  if (!chartArea) {
    return;
  }

  const gradient = ctx.createLinearGradient(
    chartArea.left,
    0,
    chartArea.right,
    chartArea.top,
  );
  gradient.addColorStop(0, "#60a6d366");
  gradient.addColorStop(1, "#168b6b66");
  return gradient;
};

const getBorderColor = (context: ScriptableContext<"line">) => {
  const { ctx, chartArea } = context.chart;

  if (!chartArea) {
    return;
  }

  const gradient = ctx.createLinearGradient(
    chartArea.left,
    0,
    chartArea.right,
    0,
  );
  gradient.addColorStop(0, "#60a6d3");
  gradient.addColorStop(1, "#168b6b");
  return gradient;
};

const externalTooltipHandler = (
  context: { chart: ChartJS; tooltip: TooltipModel<"line"> },
  tooltipEl: HTMLDivElement,
  lineEl: HTMLDivElement,
) => {
  const { chart, tooltip } = context;

  if (tooltip.body) {
    // copy paste from <Outlook /> component
    // eslint-disable-next-line lingui/no-unlocalized-strings
    const currentLegend = `
      <span class="flex h-2 w-4 flex-col self-start pt-1">
        <span class="h-0.5 w-4 bg-linear-to-r from-can-tranquil-azure to-can-mystic-aqua">&nbsp;</span>
        <span class="h-1.5 w-4 bg-[#BFDBED] inline-block">&nbsp;</span>
      </span>`;
    // copy paste from <Outlook /> component
    const nextLegend =
      // eslint-disable-next-line lingui/no-unlocalized-strings
      "<span class='h-2 w-4 inline-block border-t-2 border-dotted self-start pt-2 shrink-0 border-can-forest-teal bg-can-silver-gray'>&nbsp;</span>";

    const dateHtml = (tooltip.afterBody ?? []).reduce(
      (html: string, nextChunk: string) => html + nextChunk,
      "",
    );

    const bodyHtml = tooltip.body
      .map((body) => body.lines)
      .reduce((html, nextChunk, i) => {
        return (
          html +
          // eslint-disable-next-line lingui/no-unlocalized-strings
          "<div class='flex items-center gap-x-2'>" +
          (i === 0 ? currentLegend : nextLegend) +
          "<b>" +
          nextChunk +
          "</b>" +
          "</div>"
        );
      }, "" as string);

    // eslint-disable-next-line lingui/no-unlocalized-strings
    tooltipEl.innerHTML = `<div class="font-bold mb-1">${dateHtml}</div>${bodyHtml}`;
  }

  positionTooltip({ chart, tooltip, tooltipEl, lineEl });
};

export { Chart };
